/*
Author: Chathuraka Mallawa ArachchiP
Date: 25/09/2024
Copyright © 2024 Infix!. All rights reserved.
*/

import { Button, DropDown, Modal } from '@/components/Atoms'
import InputLabel from '@/components/Atoms/InputLabel'
import { useResponseId, useUrlType } from '@/components/hooks'
import { alerts } from '@/constants'
import { GenerateNumberRange, outsideQuestionnaire, RangePatterns } from '@/constants/constants'
import { RootState } from '@/reducers/store'
import { useValidate } from '@/state/survey/formSlice'
import {
    BasicAnswerSave,
    useUpdateAnswersSubmit,
    useUpdateOutsideQuestionnaireAnswersDispatch,
} from '@/state/survey/saveAnswerSlice'
import { Utility, Validate } from '@/utils'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import poaAnswerDetails from '../../SurveyModel/poaQuestions/poaAnswerDetails'
import { FormValues } from '../../SurveyModel/types/commonTypes'
import {
    Alert,
    DropDownOption,
    INode,
    Ioption,
    nodeConfigurations,
} from '../../SurveyModel/types/nodeTypes'

interface IDropDownNode {
    node: INode
    children: ReactNode
    name: string
    disabled: boolean
    repeatedElement?: boolean
    className?: string
    onBlur: () => void
    actionAlert?: boolean
}
const DropDownNode: React.FC<IDropDownNode> = (props: IDropDownNode) => {
    const {
        node,
        children,
        disabled,
        name,
        repeatedElement = false,
        onBlur,
        className,
    } = props
    
    // Get searchable property from node (with fallback to false)
    const searchable = (node as any).searchable || false
    const [
        AnyBloodThinningMedicationValuePopUp,
        setAnyBloodThinningMedicationValue,
    ] = useState(false)

    const { options } = node as INode
    const { answerResponseId } = useResponseId()
    const { informationPacks } = useSelector(
        (state: RootState) => state.poaSurvey
    )
    const form = useSelector((state: RootState) => state.form)
    const validateInputField = useValidate()
    const updateAnswersDispatch = useUpdateAnswersSubmit()
    //const updateExcludeData = useUpdateExcludeData()
    const updateOutsideQuestionnaireAnswersDispatch =
        useUpdateOutsideQuestionnaireAnswersDispatch()
    const { state = {} } = form[node.name]
    const [typeOptions, setTypeOptions] = useState<DropDownOption[]>([])
    const [excludeOptions, setExcludeOptions] = useState<DropDownOption[]>([])
    const [filteredOptions, setFilteredOptions] = useState<DropDownOption[]>([])
    const [searchTerm, setSearchTerm] = useState('')
    const [localValue, setLocalValue] = useState(form[node.name].value)
    const [questionName, setQuestionName] = useState('')
    const [alertExp, setAlertExp] = useState('')
    const [actionExp, setActionExp] = useState('')
    const [actionExpVal, setActionExpVal] = useState<FormValues>()
    const hasExecuted = useRef(false)

    const [open, setOpen] = useState(false)
    const { isPatient } = useUrlType()
    const { waitingListRecordId } = useParams()
    const { excludedSelectOptions } = useSelector(
        (state: RootState) => state.inputData
    )

    const generateNumberRangeConfig = useMemo(() => 
        node.configurations?.find(
            (config: nodeConfigurations) =>
                config.key === GenerateNumberRange
        ), [node.configurations]
    )

    useEffect(() => {
        if (generateNumberRangeConfig) {
            generateNumberRangFromConfig();
        }
        else if (options?.length) {
            prepareTypeOptions(options as Ioption[])
        }
    }, [options, generateNumberRangeConfig])

    const generateNumberRangFromConfig = () => {
        if (generateNumberRangeConfig) {
            const { value } = generateNumberRangeConfig;
            
            let start: number = 0;
            let end: number = 0;
            
            if (value.startsWith(RangePatterns.LastPrefix) && value.endsWith(RangePatterns.YearsSuffix)) {
                const yearCount = parseInt(value.match(/\d+/)?.[0] || '0');
                start = new Date().getFullYear() - yearCount;
                end = new Date().getFullYear();
            }
            
            const generatedOptions: DropDownOption[] = []
            for (let i = end; i >= start; i--) {
                generatedOptions.push({ value: i.toString(), label: i.toString(), description: '' })
            }
            
            // Append options to the start of the array if they exist
            const finalOptions: DropDownOption[] = (options as Ioption[])?.length
                ? [...(options as Ioption[]).map((option: Ioption) => ({ 
                    value: option.name,
                    label: option.name,
                    description: option.description
                })), ...generatedOptions]
                : generatedOptions;
            
            setTypeOptions(finalOptions)
        }
    }

    useEffect(() => {
        if (searchable) {
            const filtered = typeOptions.filter(option =>
                option.label.toLowerCase().includes(searchTerm.toLowerCase())
            )
            setFilteredOptions(filtered)
        } else {
            setFilteredOptions(typeOptions)
        }
    }, [typeOptions, searchTerm, searchable])

    useEffect(() => {
        node?.alerts?.map((alert: Alert) => {
            if (
                alert.type === alerts.alerts &&
                Utility.evaluateManually(form, alert.expression)
            ) {
                setAlertExp(alert.expression)
            }
            if (alert.type === alerts.action) {
                let newName = ''
                if (node.name.includes('-node-')) {
                    const result = node.name.split('-').slice(1).join('-')

                    const regex = /{(.*?)}/g
                    if (alert.expression) {
                        const matches = alert.expression.match(regex)
                        matches?.forEach((element: string) => {
                            const expname = element.replace(/[{}]/g, '')
                            newName = `${expname}-${result}`
                        })
                    }
                    if (form[newName])
                        setActionExpVal(
                            form[newName].value === 'false' ||
                                form[newName].value === undefined ||
                                form[newName].value === ''
                                ? false
                                : true
                        )
                    setActionExp(alert.expression)
                } else {
                    let newName = ''
                    const regex = /{(.*?)}/g
                    if (alert.expression) {
                        const matches = alert.expression.match(regex)
                        matches?.forEach((element: string) => {
                            newName = element.replace(/[{}]/g, '')
                        })
                    }
                    if (form[newName]) setActionExpVal(form[newName].value)
                    setActionExp(alert.expression)
                }
            }
        })
    }, [node?.alerts, form])

    useEffect(() => {
        let name = ''
        if (node.name.includes('-node-')) {
            name = node.name.split('-')[0]
        }
        if (excludedSelectOptions && excludedSelectOptions[name]) {
            setExcludeOptions(excludedSelectOptions[name])
        }
    }, [excludedSelectOptions])

    const handleChange = (e: any) => {
        // if (Utility.getValueFromKey(node, 'NoRepeatOptions') === 'true') {
        //     updateExcludeData(e.value, node.name)
        // }

        if (
            !isPatient &&
            node.name === 'AnyBloodThinningMedication' &&
            form['AnyBloodThinningMedication']?.value === 'Yes' &&
            e.value === 'No'
        ) {
            setAnyBloodThinningMedicationValue(true)
        } else {
            validateInputField(node.name, e.value, true)
        }
        if (node.validationDependants) {
            const nodeHash = Utility.extractNodeHash(node.name);
            node.validationDependants.forEach((element: string) => {
                const dependentNodeName = repeatedElement ? element + "-" + nodeHash : element;
                validateInputField(dependentNodeName, form[dependentNodeName].value, true)
            })
        }

        // Clear search term when an option is selected
        if (searchable) {
            setSearchTerm('')
        }
    }

    const handleInputChange = (inputValue: string) => {
        if (searchable) {
            setSearchTerm(inputValue)
            setOpen(true)
        }
    }

    useEffect(() => {
        if (
            !isPatient &&
            name &&
            form[node.name] &&
            questionName != name &&
            !hasExecuted.current
        ) {
            //poa only
            validateInputField(name, form[node.name].value, true)
            setQuestionName(name)
            hasExecuted.current = true
        }
    }, [form, questionName, node, isPatient, name, validateInputField])

    useEffect(() => {
        if (
            node.configurations?.some(
                (config: nodeConfigurations) =>
                    config.key === outsideQuestionnaire &&
                    config.value === 'true'
            )
        ) {
            if (
                informationPacks &&
                answerResponseId &&
                form[node.name].value != localValue &&
                !repeatedElement
            ) {
                const answerObj =
                    poaAnswerDetails.generateOutsideQuestionnaireAnswerSubmitFormat(
                        form,
                        node,
                        informationPacks
                    )
                if (answerObj && waitingListRecordId) {
                    updateOutsideQuestionnaireAnswersDispatch(
                        answerObj,
                        waitingListRecordId
                    )
                }
            }
            return
        }
        if (
            answerResponseId &&
            form[node.name].value != localValue &&
            !repeatedElement
        ) {
            const answers: BasicAnswerSave[] =
                poaAnswerDetails.generateQuestionAnswerSubmitFormat(form, node)
            updateAnswersDispatch(answers, answerResponseId)
            setLocalValue(form[node.name].value)
        } else if (
            answerResponseId &&
            form[node.name].value != localValue &&
            repeatedElement &&
            onBlur
        )
            onBlur()
    }, [localValue, form[node.name].value])

    const prepareTypeOptions = (options: Ioption[]) => {
        const optionList: DropDownOption[] = []
        options.map((data) => {
            const optionItem = {
                value: data.name,
                label: data.name,
                description: data.description,
            }
            optionList.push(optionItem)
        })
        setTypeOptions(optionList)
    }

    const closeAnyBloodThinningMedication = () => {
        setAnyBloodThinningMedicationValue(false)
        validateInputField('AnyBloodThinningMedication', 'Yes', true)
    }

    const approveAnyBloodThinningMedication = () => {
        setAnyBloodThinningMedicationValue(false)
        validateInputField('AnyBloodThinningMedication', 'No', true)
    }

    const handleBlur = () => {
        // Custom logic (if needed) when dropdown is blurred
        setOpen(false)
        if (searchable) {
            setSearchTerm('')
        }
    }

    return (
        <div
            id={form[node.name].value ? `${form[node.name].value}` : uuidv4()}
            className={`drop-down-wrap 
        ${Utility.getClassForWidthPx(node)}  
        ${Utility.getClassForWidth(node)}
        ${node.name}
        ${!Utility.getClassForWidthPx(node) && !Utility.getClassForWidth(node) ? 'flex-1' : ''}  
        user-input drop-down 
        question-bottom-space-1
        ${className} ${!isPatient ? 'poa-input' : 'patient-input'} 
        input-border-top 
        ${alertExp && Utility.evaluateManually(form, alertExp) ? 'red-strip' : ''} 
        ${actionExp && Utility.evaluateByValue(actionExpVal, actionExp) && form[node.name].value ? 'blue-strip' : ''}`}
        >
            <div className="user-input-body">
                <InputLabel
                    label={node.title}
                    labelClasses="input-label"
                ></InputLabel>
                <div className="drop-down-field">
                    <DropDown
                        onBlur={handleBlur}
                        form={form}
                        excludeOptions={excludeOptions}
                        menuIsOpen={open}
                        disabled={disabled}
                        isInValid={Validate.isInvalid(state)}
                        propClasses={'single-chocie-choices flex'}
                        value={
                            typeOptions &&
                            typeOptions.length &&
                            typeOptions.filter(
                                (element: DropDownOption) =>
                                    element.value === form[node.name].value
                            )
                        }
                        options={searchable ? filteredOptions : typeOptions}
                        name={node.name}
                        handleChange={handleChange}
                        configurations={node.configurations}
                        isSearchable={searchable}
                        onInputChange={searchable ? handleInputChange : undefined}
                        inputValue={searchable ? searchTerm : undefined}
                        placeholder={searchable ? (node as any).placeholderText : undefined}
                    />
                </div>
            </div>
            <div>{children}</div>

            <Modal
                propClasses="worning drop-down-popup"
                isOpen={AnyBloodThinningMedicationValuePopUp && !isPatient}
                setClose={closeAnyBloodThinningMedication}
            >
                <div>
                    Are you sure you want to change this answer to ‘No’ and
                    delete the associated details?
                </div>
                <div className="button-container">
                    <Button
                        type="tertiary"
                        text="Yes, change this answer to ‘No’"
                        icon={false}
                        handleClick={approveAnyBloodThinningMedication}
                        stateProp="enabled"
                    />
                    <Button
                        type="primary"
                        text="Cancel"
                        icon={false}
                        handleClick={closeAnyBloodThinningMedication}
                        stateProp="enabled"
                    />
                </div>
            </Modal>
        </div>
    )
}

export default DropDownNode
